/**
 * Headless board layer that derives the live drift TOPOLOGY (observed-but-
 * unmodeled call pairs) and publishes it to the board solver via
 * {@link setDriftTopology}. Mounted as a board-layer contribution so it sits
 * inside BOTH the telemetry providers (live stats) and the workspace model — the
 * two things a cross-space drift resolver needs, which straddle the solver in the
 * provider tree. It renders nothing.
 *
 * Modeled keys are derived from the MODEL, never the built board — the board now
 * contains the folded violation edges, so reading them back would treat drift as
 * "modeled", empty the topology, and oscillate. The model is the source of truth
 * for what's documented.
 */
import { useEffect, useMemo } from "react";
import { moduleKey, edgeFromKey, edgeToKey, type ArchitectureModel, type Module } from "@archlang/scene/arch-model";
import { useWorkspaceModel } from "../../workbench/WorkspaceModelContext.js";
import { useLiveEdgeStats } from "../liveStatsStore.js";
import { normServiceName, trafficEdgeKey } from "../liveTrafficStore.js";
import { setDriftTopology } from "../driftTopologyStore.js";
import { driftTopology } from "./conformanceEdges.js";

interface ModelMaps {
  /** normServiceName(name) → moduleKey — resolves an observed service to a module. */
  readonly nameToKey: Map<string, string>;
  /** moduleKey → name — resolves an edge endpoint back to its telemetry name. */
  readonly keyToName: Map<string, string>;
}

function buildModelMaps(model: ArchitectureModel): ModelMaps {
  const nameToKey = new Map<string, string>();
  const keyToName = new Map<string, string>();
  const visit = (m: Module) => {
    const k = moduleKey(m);
    nameToKey.set(normServiceName(m.name), k);
    keyToName.set(k, m.name);
    for (const c of m.children ?? []) visit(c);
  };
  for (const m of model.modules) visit(m);
  return { nameToKey, keyToName };
}

export function DriftTopologyPublisher(): null {
  const { model, edges } = useWorkspaceModel();
  const stats = useLiveEdgeStats();

  const maps = useMemo(() => (model ? buildModelMaps(model) : null), [model]);

  // Modeled service-pair keys (the stats-map key space), from the MODEL edges.
  const modeledKeys = useMemo(() => {
    const set = new Set<string>();
    if (!maps) return set;
    for (const e of edges) {
      const from = maps.keyToName.get(edgeFromKey(e));
      const to = maps.keyToName.get(edgeToKey(e));
      if (from && to) set.add(trafficEdgeKey(from, to));
    }
    return set;
  }, [edges, maps]);

  const topology = useMemo(
    () => (maps ? driftTopology(modeledKeys, stats, (n) => maps.nameToKey.get(n)) : []),
    [modeledKeys, stats, maps],
  );

  useEffect(() => {
    // setDriftTopology dedupes on shape, so a metric-only poll is a no-op and the
    // board does not re-solve.
    setDriftTopology(topology);
  }, [topology]);

  return null;
}
